import {UISprite} from "./UISprite";
import {UIText} from "./UIText";
import {UIButton} from "./UIButton";
import {Core} from "../../../Base/Core";
import {gsap} from "gsap";
import {UIBase} from "./UIBase";

const BACKGROUND_SKIN = {
    "green": ['rgb(25, 59, 38)', 'rgb(38, 100, 58)'],
    "orange": ['rgb(148, 58, 0)', 'rgb(170, 113, 0)'],
    "magenta": ['rgb(107, 58, 203)', 'rgb(170, 113, 255)'],
};

function createGradientTexture(skin = "green") {
    skin = BACKGROUND_SKIN[skin] ? skin : "green";
    let id = `popup-back-skin-${skin}`;
    if (!PIXI.utils.TextureCache[id]) {
        const canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = Core.app.viewport.height;
        const ctx = canvas.getContext('2d');
        const grd = ctx.createLinearGradient(0, 0, 0, canvas.height);
        const [start, end] = BACKGROUND_SKIN[skin] || BACKGROUND_SKIN["green"];
        grd.addColorStop(0, start);
        grd.addColorStop(1, end);
        ctx.fillStyle = grd;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        let tx = PIXI.Texture.from(canvas);
        PIXI.Texture.addToCache(tx, id);
    }
    return PIXI.utils.TextureCache[id];
}

class PopupBack extends PIXI.Graphics {
    _rect = new PIXI.RoundedRectangle(0, 0, 1, 1, 50);
    skin = "green";

    get radius() {
        return this._rect.radius;
    }

    set radius(val) {
        this._rect.radius = val;
        this.fitLayout();
    }

    constructor(skin = "green") {
        super();
        this.skin = skin;
    }

    getShape() {
        return this._rect.clone();
    }

    fitLayout(rect = this._rect) {
        this._rect.x = rect.x;
        this._rect.y = rect.y;
        this._rect.width = rect.width;
        this._rect.height = rect.height;

        this.position.copyFrom(this._rect);
        const tx = createGradientTexture(this.skin);
        const shape = this.getShape();
        shape.x = shape.y = 0;
        this.clear()
            .beginTextureFill({texture: tx, alpha: 0.9})
            .drawShape(shape)
        ;
    }
}

class PopupHeaderBack extends PIXI.Graphics {
    _rect = new PIXI.Rectangle();
    fitLayout(rect = this._rect) {
        this._rect.copyFrom(rect);
        this.position.copyFrom(this._rect);
        this.clear()
            .beginFill(0x000000, 0.5)
            .drawRect(0, 0, this._rect.width, this._rect.height)
        ;
    }
}

const VISIBLE_INSTANCES = [];

export class UICommonPopup extends UIBase {
    _popupRect = new PIXI.Rectangle(0, 0, 1, 1);

    Awake({ skin = "green", masked = false, title = "", closeButton = false, sound = "PopupShow" } = {}) {
        // Popups are hidden by default
        this.visible = false;
        this.Active = false;

        // A layer to prevent popup click-through
        this.antiClick = this.addChild(new UISprite("particles/rect"));
        this.antiClick.interactive = true;
        this.antiClick.tint = 0x000000;
        this.antiClick.alpha = 0.75;

        this.wrapper = this.addChild(new PIXI.Container());

        // Default background
        this.back = this.wrapper.addChild(new PopupBack(skin));
        this.content = this.wrapper.addChild(new PIXI.Container());
        this.header = this.content.addChild(new PopupHeaderBack());
        this.header.SetActive(!!title);

        this.title = this.content.addChild(new UIText(title || ""));
        this.title.SetActive(!!title);

        this.closeBtn = this.content.addChild(new UIButton("button-close"));
        this.closeBtn.alpha = 0.5;
        this.closeBtn.SetActive(!!closeButton);

        this.masked = masked;
        this.sound = sound;

        this.closeBtn.on("pointertap", () => this.Hide());
    }

    Init() {
        super.Init();
    }

    setPopupSize(w, h) {
        this.setPopupRect(new PIXI.Rectangle(
            (Core.app.viewport.width - w) * 0.5,
            (Core.app.viewport.height - h) * 0.5,
            w, h
        ));
    }

    setPopupRect(rect) {
        this._popupRect.copyFrom(rect);
    }

    getPopupRect() {
        return this._popupRect.clone();
    }

    getContentSize(trimHeader = false) {
        let res = this._popupRect.clone();
        res.x = res.y = 0;
        if (trimHeader) {
            let headerHeight = this.header.height;
            res.y += headerHeight;
            res.height -= headerHeight;
        }
        return res;
    }

    _renderMask() {

        let resolution = 1 / Core.app.renderer.resolution;
        let shape = this.back.getShape();
        shape.x *= resolution;
        shape.y *= resolution;
        shape.width *= resolution;
        shape.height *= resolution;
        let mask = new PIXI.Graphics().beginFill(0xFFFFFF, 1).drawShape(shape);

        this.back.mask = mask;
        this.header.mask = mask;
        this.content.mask = this.masked ? mask : null;
    }

    fitLayout(viewport = Core.app.viewport) {

        if (this._popupRect.width === 1 && this._popupRect.height === 1) {
            this.setPopupRect(viewport.clone().pad(-viewport.width * 0.05));
        }

        this.antiClick.position.set(viewport.width * 0.5, viewport.height * 0.5);
        this.antiClick.width = viewport.width;
        this.antiClick.height = viewport.height;

        this.wrapper.position.set(0, 0);

        this.back.fitLayout(this._popupRect);
        this.content.position.copyFrom(this._popupRect);

        let contentRect = this.getContentSize();
        let headerRect = new PIXI.Rectangle(0, 0, contentRect.width, contentRect.width * 0.17);
        this.fitHeader(headerRect);

        this._renderMask();
    }

    fitHeader(headerRect) {
        this.header.fitLayout(headerRect);

        let rect = headerRect.clone();
        rect.width = headerRect.width * 0.55;
        rect.height = headerRect.height * 0.9;
        rect.x = (headerRect.width - rect.width) * 0.5;
        rect.y = (headerRect.height - rect.height) * 0.5;
        this.title.fitLayout(rect);

        rect.width = headerRect.height * 0.5;
        rect.height = headerRect.height * 0.5;
        rect.x = (headerRect.width - rect.width) * 0.95;
        rect.y = (headerRect.height - rect.height) * 0.5;
        this.closeBtn.fitRect(rect);
    }

    _updateStackVisibility() {
        let currentIdx = VISIBLE_INSTANCES.indexOf(this);
        if (this.Active) {
            if (currentIdx < 0) VISIBLE_INSTANCES.push(this);
        }
        else {
            if (currentIdx >= 0) {
                VISIBLE_INSTANCES.splice(currentIdx, 1);
                currentIdx = -1;
            }
        }
        if (currentIdx < 0) {
            currentIdx = VISIBLE_INSTANCES.length - 1;
        }
        for (let i = 0; i < VISIBLE_INSTANCES.length; i++) {
            VISIBLE_INSTANCES[i].x = i === currentIdx ? 0 : -Core.app.viewport.width * 3;
        }
    }

    SetActive(value) {
        super.SetActive(value);
        this._updateStackVisibility();
    }

    Show() {
        super.Show();
        this.fitLayout();
        gsap.timeline()
            .fromTo(this.wrapper, {alpha: 0}, { alpha: 1, duration: 0.3 }, 0)
        ;
        if (this.sound) {
            Core.Audio.Play(this.sound);
        }
        this._updateStackVisibility();
    }

    Hide() {
        super.Hide();
        this._updateStackVisibility();
    }
}
